// Замеры стадии в коротком пути (docs/architecture/BATCH_RECORDS.md §6):
// таблица «параметр × замер», новый замер, аннулирование с причиной.
// Все наборы заказа приходят одним запросом; компонент показывает наборы
// своей стадии, поэтому несколько стадий на странице делят один запрос.

import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { ApiError, measurementsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { MeasurementParam, MeasurementReading, MeasurementRound, MeasurementSet } from "@/lib/types";

const STATUS_PILL: Record<MeasurementSet["status"], string> = {
  empty: "pill",
  incomplete: "pill pill-warn",
  ok: "pill pill-ok",
  nok: "pill pill-danger",
};

function errorText(t: TFunction, e: unknown): string {
  if (e instanceof ApiError) {
    return t(`measurements.errors.${e.code}`, { defaultValue: `${e.code}: ${e.message}` });
  }
  return e instanceof Error ? e.message : t("measurements.errors.failed");
}

/** Число из поля ввода; запятая — как десятичный разделитель. */
function parseNumber(s: string | undefined): number | null {
  const v = s?.trim().replace(",", ".");
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const hasLimits = (p: MeasurementParam) => p.type === "number" && (p.norm.lo !== null || p.norm.hi !== null);

export function StageMeasurements({ orderId, stageCode }: { orderId: string; stageCode: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["mo-measurements", orderId],
    queryFn: () => measurementsApi.list(orderId),
    refetchInterval: 5000,
  });
  if (q.isError) return <p className="mt-2 text-xs text-danger">{errorText(t, q.error)}</p>;
  const sets = (q.data?.sets ?? []).filter((s) => s.stage_code === stageCode);
  if (sets.length === 0) return null;
  return (
    <div className="mt-2 space-y-3">
      {sets.map((s) => (
        <SetCard key={s.key} orderId={orderId} set={s} />
      ))}
    </div>
  );
}

function SetCard({ orderId, set }: { orderId: string; set: MeasurementSet }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Подпись стадии зависит от замеров — чеклист стадий обновляем вместе с ними.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["mo-measurements", orderId] });
    qc.invalidateQueries({ queryKey: ["mo-short-flow", orderId] });
  };
  const voidRound = useMutation({
    mutationFn: (round: number) => measurementsApi.void(orderId, set.key, round, reason.trim()),
    onSuccess: () => {
      setVoiding(null);
      setReason("");
      setErr(null);
      refresh();
    },
    onError: (e) => setErr(errorText(t, e)),
  });

  const active = set.rounds.filter((r) => !r.voided).length;
  const limitReached = set.max_rounds !== null && active >= set.max_rounds;
  const reading = (r: MeasurementRound, param: string) => r.readings.find((x) => x.param === param);
  const mark = (ok: boolean) => (ok ? set.marks.ok : set.marks.nok);
  const cellClass = (r: MeasurementRound, ok: boolean | undefined) =>
    `px-2 py-1 font-mono tabular-nums ${
      r.voided ? "line-through text-text-muted" : ok === false ? "text-danger font-semibold" : ok ? "text-ok" : ""
    }`;
  const shown = (x: MeasurementReading) => (x.value !== null ? String(x.value) : mark(x.ok));
  const cellTitle = (x: MeasurementReading) =>
    [`${t("measurements.norm")}: ${x.norm || "—"}`, x.note].filter(Boolean).join("\n");
  const roundTitle = (r: MeasurementRound) =>
    r.voided
      ? t("measurements.voided", {
          at: formatDateTime(r.voided.at),
          by: r.voided.by_name ?? "—",
          reason: r.voided.reason,
        })
      : (r.comment ?? undefined);

  return (
    <div className="rounded-md border border-border p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{set.title}</h3>
        {set.form && <span className="font-mono text-xs text-text-muted">{set.form}</span>}
        <span className={`${STATUS_PILL[set.status]} text-xs`}>{t(`measurements.status.${set.status}`)}</span>
        {set.min_rounds > 1 && (
          <span className="text-xs text-text-muted">{t("measurements.minRounds", { n: set.min_rounds })}</span>
        )}
        {set.max_rounds !== null && (
          <span className="text-xs text-text-muted">{t("measurements.maxRounds", { n: set.max_rounds })}</span>
        )}
        <span className="flex-1" />
        {set.editable && !adding && (
          <button
            className="btn text-xs px-2 py-1"
            disabled={limitReached}
            title={limitReached ? t("measurements.limitReached") : undefined}
            onClick={() => {
              setErr(null);
              setAdding(true);
            }}
          >
            {t("measurements.add")}
          </button>
        )}
      </div>
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-muted">
              <th className="px-2 py-1 font-normal">{t("measurements.param")}</th>
              <th className="px-2 py-1 font-normal">{t("measurements.norm")}</th>
              {set.rounds.map((r) => (
                <th
                  key={r.id}
                  className={`px-2 py-1 font-normal align-bottom ${r.voided ? "line-through opacity-60" : ""}`}
                  title={roundTitle(r)}
                >
                  <div className="font-mono text-text-secondary">{t("measurements.round", { n: r.round })}</div>
                  <div>{formatDateTime(r.measured_at)}</div>
                  <div>{r.measurer_name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {set.params.map((p, i) => (
              <Fragment key={p.key}>
                {p.group !== null && p.group !== set.params[i - 1]?.group && (
                  <tr>
                    <td colSpan={2 + set.rounds.length} className="px-2 pt-2 text-xs font-semibold text-text-secondary">
                      {p.group}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-border">
                  <td className="px-2 py-1">
                    {p.label}
                    {p.unit ? `, ${p.unit}` : ""}
                    <span className={`ml-2 text-xs ${p.kind === "process" ? "text-info" : "text-text-muted"}`}>
                      {t(`measurements.kind.${p.kind}`)}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-xs text-text-secondary">{p.norm.text || "—"}</td>
                  {set.rounds.map((r) => {
                    const x = reading(r, p.key);
                    return (
                      <td key={r.id} className={cellClass(r, x?.ok)} title={x ? cellTitle(x) : undefined}>
                        {x ? shown(x) : "—"}
                        {x?.note ? " *" : ""}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            ))}
            <tr className="border-t border-border-strong">
              <td colSpan={2} className="px-2 py-1 text-xs text-text-muted">
                {t("measurements.result")}
              </td>
              {set.rounds.map((r) => (
                <td key={r.id} className={cellClass(r, r.ok)}>
                  {mark(r.ok)}
                  {set.editable && !r.voided && (
                    <button
                      type="button"
                      className="ml-2 font-sans text-xs font-normal text-text-muted underline hover:text-danger"
                      onClick={() => {
                        setErr(null);
                        setReason("");
                        setVoiding(r.round);
                      }}
                    >
                      {t("measurements.void")}
                    </button>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {voiding !== null && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
          <label className="text-sm" htmlFor={`void-${set.key}`}>
            {t("measurements.voidReason", { n: voiding })}
          </label>
          <input
            id={`void-${set.key}`}
            className="flex-1 min-w-48 px-2 py-1 rounded-md bg-surface border border-border text-sm"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            className="btn btn-danger text-xs px-2 py-1"
            disabled={!reason.trim() || voidRound.isPending}
            onClick={() => voidRound.mutate(voiding)}
          >
            {t("measurements.void")}
          </button>
          <button className="btn text-xs px-2 py-1" onClick={() => setVoiding(null)}>
            {t("common.cancel")}
          </button>
        </div>
      )}

      {adding && (
        <NewRoundForm
          orderId={orderId}
          set={set}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function NewRoundForm({
  orderId,
  set,
  onDone,
  onCancel,
}: {
  orderId: string;
  set: MeasurementSet;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [oks, setOks] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [comment, setComment] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      measurementsApi.add(orderId, set.key, {
        readings: set.params.map((p) => ({
          param: p.key,
          ...(p.type === "number" ? { value: parseNumber(values[p.key]) } : {}),
          ...(hasLimits(p) ? {} : { ok: oks[p.key] }),
          note: notes[p.key]?.trim() || null,
        })),
        comment: comment.trim() || null,
      }),
    onSuccess: onDone,
    onError: (e) => setErr(errorText(t, e)),
  });

  // Предпросмотр того, что посчитает сервер, по границам нормы.
  const preview = (p: MeasurementParam): boolean | null => {
    const v = parseNumber(values[p.key]);
    if (v === null) return null;
    return (p.norm.lo === null || v >= p.norm.lo) && (p.norm.hi === null || v <= p.norm.hi);
  };
  const ready = set.params.every(
    (p) => (p.type !== "number" || parseNumber(values[p.key]) !== null) && (hasLimits(p) || oks[p.key] !== undefined),
  );

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="text-sm font-semibold">{t("measurements.add")}</div>
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      {set.params.map((p) => {
        const pv = hasLimits(p) ? preview(p) : null;
        return (
          <div
            key={p.key}
            className="grid grid-cols-1 items-center gap-2 md:grid-cols-[minmax(12rem,1fr)_8rem_minmax(14rem,auto)_minmax(10rem,1fr)]"
          >
            <div className="text-sm">
              {p.label}
              {p.unit ? `, ${p.unit}` : ""}
              <div className="font-mono text-xs text-text-muted">
                {t("measurements.norm")}: {p.norm.text || "—"}
              </div>
            </div>
            <div>
              {p.type === "number" && (
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={p.label}
                  className="w-full px-2 py-1 rounded-md bg-surface border border-border text-sm font-mono"
                  value={values[p.key] ?? ""}
                  onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
                />
              )}
            </div>
            <div>
              {hasLimits(p) ? (
                pv !== null && (
                  <span className={`text-xs ${pv ? "text-ok" : "text-danger font-semibold"}`}>
                    {pv ? t("measurements.conforms") : t("measurements.notConforms")}
                  </span>
                )
              ) : (
                <OkToggle value={oks[p.key]} onChange={(v) => setOks({ ...oks, [p.key]: v })} />
              )}
            </div>
            <input
              placeholder={t("measurements.note")}
              aria-label={`${p.label}: ${t("measurements.note")}`}
              className="w-full px-2 py-1 rounded-md bg-surface border border-border text-sm"
              value={notes[p.key] ?? ""}
              maxLength={500}
              onChange={(e) => setNotes({ ...notes, [p.key]: e.target.value })}
            />
          </div>
        );
      })}
      <textarea
        placeholder={t("measurements.comment")}
        className="w-full px-2 py-1 rounded-md bg-surface border border-border text-sm"
        rows={2}
        maxLength={2000}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <button className="btn btn-primary text-sm" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? t("measurements.saving") : t("measurements.save")}
        </button>
        <button className="btn text-sm" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        {!ready && <span className="text-xs text-text-muted">{t("measurements.fillAll")}</span>}
      </div>
    </div>
  );
}

function OkToggle({ value, onChange }: { value: boolean | undefined; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex gap-1">
      <button
        type="button"
        aria-pressed={value === true}
        className={`btn text-xs px-2 py-1 ${value === true ? "btn-primary" : ""}`}
        onClick={() => onChange(true)}
      >
        {t("measurements.conforms")}
      </button>
      <button
        type="button"
        aria-pressed={value === false}
        className={`btn text-xs px-2 py-1 ${value === false ? "btn-danger font-semibold" : ""}`}
        onClick={() => onChange(false)}
      >
        {t("measurements.notConforms")}
      </button>
    </div>
  );
}
